import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Divider, ThemeProvider, Grid, IconButton, InputAdornment } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Eye, EyeOff } from 'lucide-react';
import { useSignUp, useSignInWithGoogle } from '@lingaai/shared-hooks';
import { getCurrentUser } from '@lingaai/supabase-client';
import { isFreeEmail } from 'free-email-domains-list';
import { lightTheme } from '@lingaai/shared-theme';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

/**
 * Custom blocked email domains
 * Add any domains you want to block in addition to the free email providers
 * (e.g., competitors, known spam domains, etc.)
 */
const CUSTOM_BLOCKED_DOMAINS: string[] = [
  // Add custom domains to block here
  // Example: 'competitor.com', 'spam-domain.com'
  'altvia.com',
  'app-x.com',
  'marlinequitypartners.com',
  'aspenpeakpartners.com',
  'oakmountainpartners.com',
  'dealcloud.com',
  'affinity.com',
  'salesforce.com',
];

/**
 * Validates if an email is blocked
 * Checks against both free email providers (Gmail, Yahoo, etc.) and custom blocked domains
 *
 * @param email - The email address to validate
 * @returns true if the email is blocked, false if it's allowed
 */
function isBlockedEmail(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase();

  // Check if it's a free email provider
  if (isFreeEmail(email)) {
    return true;
  }

  // Check if it's in our custom blocked domains list
  if (domain && CUSTOM_BLOCKED_DOMAINS.includes(domain)) {
    return true;
  }

  return false;
}

export function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const signUp = useSignUp();
  const signInWithGoogle = useSignInWithGoogle();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate business email
    if (isBlockedEmail(email)) {
      setError('Please use a business email address (not Gmail, Yahoo, etc.)');
      return;
    }

    try {
      await signUp.mutateAsync({
        email,
        password,
        firstName,
        lastName,
        organization,
      });

      // Fetch user data to determine status
      const currentUser = await getCurrentUser();

      // Redirect based on user status
      if (currentUser?.status === 'pending') {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle.mutateAsync();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign up with Google');
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box display="flex" minHeight="100vh" bgcolor="grey.100" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Animated geometric background */}
        <Box
          component="svg"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 0.6,
          }}
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#174a43" stopOpacity="1">
                <animate attributeName="stop-color" values="#174a43; #22C55E; #174a43" dur="15s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#22C55E" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#22C55E; #10B981; #22C55E" dur="15s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="1">
                <animate attributeName="stop-color" values="#F59E0B; #FCD34D; #F59E0B" dur="18s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#FCD34D" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#FCD34D; #F59E0B; #FCD34D" dur="18s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22C55E" stopOpacity="1">
                <animate attributeName="stop-color" values="#22C55E; #10B981; #22C55E" dur="20s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FCD34D" stopOpacity="1">
                <animate attributeName="stop-color" values="#FCD34D; #D97706; #FCD34D" dur="16s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#D97706" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <polygon className="polygon1" points="0,0 600,100 400,500" fill="url(#gradient1)" opacity="0.15" />
          <polygon className="polygon2" points="1200,0 800,300 1200,600" fill="url(#gradient2)" opacity="0.12" />
          <polygon className="polygon3" points="200,800 700,600 300,400" fill="url(#gradient3)" opacity="0.1" />
          <polygon className="polygon4" points="1000,800 1200,500 800,700" fill="url(#gradient4)" opacity="0.08" />
        </Box>
        <style>
          {`
          @keyframes morph1 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(50px, -30px); }
          }
          @keyframes morph2 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-40px, 40px); }
          }
          @keyframes morph3 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(30px, 50px); }
          }
          @keyframes morph4 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-30px, -40px); }
          }
          .polygon1 { animation: morph1 20s ease-in-out infinite; }
          .polygon2 { animation: morph2 25s ease-in-out infinite; }
          .polygon3 { animation: morph3 30s ease-in-out infinite; }
          .polygon4 { animation: morph4 22s ease-in-out infinite; }
        `}
        </style>

        <Box sx={{ width: '100%', px: { xs: 2, md: 6 }, position: 'relative', zIndex: 1, py: 6 }}>
          {/* Full Width Headline */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Stop chasing portfolio companies. Get complete data effortlessly.
            </Typography>
          </Box>

          <Grid container spacing={6}>
            {/* Left Column - Value Proposition */}
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                {/* Key Stat */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    bgcolor: 'rgba(251, 191, 36, 0.15)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(251, 191, 36, 0.3)',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                    }}
                  >
                    Save 12-40 hours per quarter
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
                    Stop chasing portfolio companies for data. Linga AI extracts metrics automatically and follows up on missing information.
                  </Typography>
                </Box>

                {/* Pricing Anchor */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    $40/portco vs competitors' $1,500
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                    Example: $160/portco/year for quarterly reporting
                  </Typography>
                </Box>

                {/* Trust Element */}
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    No credit card • Cancel anytime
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6, color: 'text.secondary' }}>
                    14-day free trial with full access. Test with 1 portfolio company and see results immediately.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Signup Form */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Card sx={{ p: 4, maxWidth: 450, width: '100%' }}>
                <Typography variant="h4" gutterBottom>
                  Start Your 14-Day Free Trial
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} margin="normal" required />
                  <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} margin="normal" required />
                  <TextField fullWidth label="Organization" value={organization} onChange={(e) => setOrganization(e.target.value)} margin="normal" required />
                  <TextField
                    fullWidth
                    label="Business Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    helperText="You must use your company email address"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                            {showPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button fullWidth variant="contained" type="submit" disabled={signUp.isPending} sx={{ mt: 2 }}>
                    {signUp.isPending ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>

                <Divider sx={{ my: 2 }}>OR</Divider>

                <Button fullWidth variant="outlined" onClick={handleGoogleSignUp} disabled={signInWithGoogle.isPending} startIcon={<GoogleIcon />}>
                  Sign up with Google
                </Button>

                <Typography sx={{ mt: 2 }}>
                  Already have an account? <Link to="/login">Sign in</Link>
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
